import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Toolbar,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RefreshIcon from '@mui/icons-material/Refresh';
import { exportToJSON } from '../services/api';
import { useNavigate } from 'react-router-dom';
import GoldIngotLoader from './GoldIngotLoader';

function CategoryList({ categories, loading }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `amazon-categories-${timestamp}.json`;
    exportToJSON({
      exportTime: new Date().toISOString(),
      totalCategories: categories.length,
      categories: filteredCategories,
    }, filename);
  };

  const handleViewProducts = (categoryId) => {
    // Navigate to Best Sellers page with category ID parameter
    navigate(`/best-sellers?categoryId=${categoryId}`);
  };

  const handleRescrape = async (category) => {
    if (!window.confirm(`Do you want to re-scrape category "${category.nameEn}"?\n\nThis will trigger the scraper to fetch fresh data from Amazon.`)) {
      return;
    }

    // TODO: Implement re-scrape logic
    alert('Re-scrape feature will be implemented soon!\n\nThis will trigger: node server/scrapeByConfig.js');
  };

  // Filter categories
  const filteredCategories = categories.filter((category) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      category.nameEn?.toLowerCase().includes(term) ||
      category.nameLocal?.toLowerCase().includes(term) ||
      category.nodeId?.toLowerCase().includes(term)
    );
  });

  // Sort: by product count descending
  const sortedCategories = [...filteredCategories].sort((a, b) => (b.productCount || 0) - (a.productCount || 0));

  // Paginated data
  const paginatedCategories = sortedCategories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <GoldIngotLoader minHeight={300} />
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No data available, please search for categories first
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3}>
      <Toolbar sx={{ pl: 2, pr: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" component="div">
            Popular Categories
            <Chip
              label={`${filteredCategories.length} categories`}
              color="primary"
              size="small"
              sx={{ ml: 2 }}
            />
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />

          <Tooltip title="Export as JSON">
            <IconButton onClick={handleExport} color="primary">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.light' }}>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Rank</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Category Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Marketplace</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Node ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'right' }}>
                Products <TrendingUpIcon sx={{ fontSize: 18, verticalAlign: 'middle', ml: 0.5 }} />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white' }}>Last Scraped</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCategories.map((category, index) => {
              const actualRank = page * rowsPerPage + index + 1;
              const isTopCategory = actualRank <= 3;

              return (
                <TableRow
                  key={category.nodeId || index}
                  sx={{
                    '&:hover': { bgcolor: 'action.hover' },
                    bgcolor: isTopCategory ? 'action.selected' : 'inherit',
                  }}
                >
                  <TableCell>
                    <Chip
                      label={actualRank}
                      color={isTopCategory ? 'primary' : 'default'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {category.nameEn || category.nameLocal || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={category.marketplace || 'N/A'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {category.nodeId || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={(category.productCount || 0).toLocaleString()}
                      color={(category.productCount || 0) > 0 ? 'success' : 'default'}
                      variant={(category.productCount || 0) > 0 ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {category.lastScrapedAt ? (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {new Date(category.lastScrapedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="View Products">
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<ShoppingCartIcon />}
                            onClick={() => handleViewProducts(category.id)}
                            disabled={!category.productCount || category.productCount === 0}
                          >
                            View
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Re-scrape Category">
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<RefreshIcon />}
                          onClick={() => handleRescrape(category)}
                        >
                          Re-scrape
                        </Button>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={filteredCategories.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
      />
    </Paper>
  );
}

export default CategoryList;
